import React, { FC } from "react";
import { Card, Row, Col, ProgressBar, OverlayTrigger, Tooltip } from "react-bootstrap";
import TotalQuestionsIcon from "../../../icons/totalquestions.svg"
import AIGeneratedIcon from "../../../icons/Aigenerated.svg"
import CustomQuestion from "../../../icons/customtagQuestions.svg";
import { CheckCircle, XCircle, HourglassIcon } from "lucide-react";
import "./index.scss";
import ResponseTimeAnalysis from "./reponseTimeAnalysis";
import { resolveLogoUrl } from "../../../utils";

interface StateProps {
    responseAnalysisData: any | null | undefined;
    responseAnalysisThemeData: any | null | undefined;
    auditID: any | null | undefined;
}

const Analytics: FC<StateProps> = ({ responseAnalysisData, responseAnalysisThemeData, auditID }) => {


    return (
        <Card.Body className="analyticsContainer">
            <Row>
                {/* LEFT SECTION */}
                <Col md={6} className="leftSection">
                    <h6
                        className="fw-bold mb-4"
                        style={{ fontSize: "15px", color: "#102250" }}
                    >
                        Question Insights by Theme
                    </h6>

                    {/* Summary Cards */}
                    <Row className="g-3 mb-3">
                        <Col>
                            <Card className="summaryCard">
                                <h6>{responseAnalysisThemeData?.totalQuestions}</h6>
                                <div>
                                    <p>Total Questions</p>
                                    <img src={TotalQuestionsIcon} alt="info" />
                                </div>
                            </Card>
                        </Col>
                        <Col>
                            <Card className="summaryCard">
                                <h6>{responseAnalysisThemeData?.aiGeneratedQuestions}</h6>
                                <div>
                                    <p>AI Generated</p>
                                    <img src={AIGeneratedIcon} alt="info" />
                                </div>
                            </Card>
                        </Col>
                        <Col>
                            <Card className="summaryCard">
                                <h6>{responseAnalysisThemeData?.customQuestions}</h6>
                                <div>
                                    <p>Custom</p>
                                    <img src={CustomQuestion} alt="info" />
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Legend */}
                    <div className="d-flex align-items-center gap-3 my-4">
                        <div className="d-flex align-items-center gap-2">
                            <span className="legendDot" style={{ background: "#53629E" }}></span>
                            <span className="legendText">AI Generated</span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <span className="legendDot" style={{ background: "#87BAC3" }}></span>
                            <span className="legendText">Custom Questions</span>
                        </div>
                    </div>

                    {/* Theme 1 */}
                    <Col>
                        {responseAnalysisThemeData?.themeAnalysisList?.map((theme: any, index: any) => {
                            const aiPercent = (theme.aiGeneratedThemeQuestions / theme?.totalThemeQuestions) * 100;
                            const customPercent = (theme.customThemeQuestions / theme?.totalThemeQuestions) * 100;

                            return (
                                <div key={index} className="mb-3">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="themeLabel">{theme?.themeName}</div>
                                        <div className="themeCount">{theme?.totalThemeQuestions} Questions</div>
                                    </div>


                                    <div className="themeBar">

                                        {/* AI segment */}
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip className="analyticsCustomTooltip">
                                                    <div className="fw-semibold text-start">{theme.themeName}</div>
                                                    <div className="text-start">AI Questions: {theme.aiGeneratedThemeQuestions}</div>
                                                </Tooltip>
                                            }
                                        >
                                            <div
                                                className={"barAI"}
                                                style={{ width: `${aiPercent}%` }}
                                            />
                                        </OverlayTrigger>

                                        {/* Custom segment */}
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip className="analyticsCustomTooltip">
                                                    <div className="fw-semibold text-start">{theme.themeName}</div>
                                                    <div className="text-start">Custom Questions: {theme.customThemeQuestions}</div>
                                                </Tooltip>
                                            }
                                        >
                                            <div
                                                className={"barCustom"}
                                                style={{ width: `${customPercent}%` }}
                                            />
                                        </OverlayTrigger>

                                    </div>

                                </div>
                            );
                        })}
                    </Col>
                </Col>

                {/* RIGHT SECTION */}
                <Col md={6} className="rightSection">
                    <div className="d-flex justify-content-between">
                        <h6
                            className="fw-bold mb-4"
                            style={{ fontSize: "15px", color: "#102250" }}
                        >
                            Question Response Analysis
                        </h6>
                        <small className="fw-semibold">Total Questions: {responseAnalysisData?.totalQuestions ?? "-"}</small>
                    </div>

                    {/* ORG 1 */}
                    {responseAnalysisData?.organizations && responseAnalysisData?.organizations.map((item: any, index: any) => {
                        const total = item.satisfied + item.notSatisfied + item.awaitingResponse;

                        const satisfiedPct = (item.satisfied / total) * 100;
                        const notSatisfiedPct = (item.notSatisfied / total) * 100;
                        const awaitingPct = (item.awaitingResponse / total) * 100;

                        return (
                            <Card className="responseCard mb-4" key={index}>
                                {/* Header */}
                                <div className="orgHeader">
                                    <div className="orgIcon"><img src={resolveLogoUrl(item?.organizationLogo) ?? undefined} className="orgIconsAnalytics" alt={item.name} /></div>
                                    <div className="fw-semibold">{item.name}</div>
                                </div>

                                {/* Progress Bars */}
                                <ProgressBar className="orgProgressBar">
                                    <ProgressBar now={satisfiedPct} className="satisfied" />
                                    <ProgressBar now={notSatisfiedPct} className="notSatisfied" />
                                    <ProgressBar now={awaitingPct} className="awaiting" />
                                </ProgressBar>

                                {/* Summary Row */}
                                <div className="statusRow">
                                    <div style={{ color: "green", padding: "3px", background: "#FCFFF9", borderRadius: "8px" }}>
                                        <span><CheckCircle className="me-1" size={16} style={{ stroke: "green" }} /> Satisfied</span>&nbsp;&nbsp;<strong>{item.satisfied}</strong>
                                    </div>

                                    <div style={{ color: "red", padding: "3px", background: "#FFF9F9", borderRadius: "8px" }}>
                                        <span><XCircle className="me-1" size={16} style={{ stroke: "red" }} /> Not Satisfied</span>&nbsp;&nbsp;<strong>{item.notSatisfied}</strong>
                                    </div>

                                    <div style={{ color: "orange", padding: "3px", background: "#FFFBF9", borderRadius: "8px" }} >
                                        <span><HourglassIcon className="me-1" size={16} style={{ stroke: "orange" }} /> Awaiting</span>&nbsp;&nbsp;<strong>{item.awaitingResponse}</strong>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </Col>
            </Row>
            <ResponseTimeAnalysis responseAnalysisData={responseAnalysisData} auditID={auditID} />
        </Card.Body >
    );
};

export default Analytics;
